import { Box, Button, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import startOfDay from 'date-fns/startOfDay';
import startOfWeek from 'date-fns/startOfWeek';
import lodash from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { NotificationType, useAcknowledgeAllNotificationsMutation, useInfiniteIdentityNotificationsQuery } from '../../../../../generated/core-api/graphql-react-query';
import NotificationBox from './NotificationBox';
import EmptyState from './empty';
import Loader from './loader';

export default function NotificationList(props: { onClose: () => void }) {
  const { onClose } = props;
  const queryClient = useQueryClient();
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('widgets');

  const notifications = useInfiniteIdentityNotificationsQuery('nextToken', { nextToken: undefined }, {
    getNextPageParam: (lastPage) => {
      const { nextToken } = lastPage.queryIdentityNotifications;
      if (nextToken) {
        return { nextToken };
      }
      return undefined;
    },
  });

  const groupedNotifications = React.useMemo(() => {
    const todayTimestamp = startOfDay(new Date()).getTime();
    const thisWeekTimestamp = startOfWeek(new Date()).getTime();
    const allNotifications = lodash.flatten(notifications.data?.pages.map((page) => page.queryIdentityNotifications.items))
      .filter((notification) => {
        switch (notification.event.eventType) {
          case NotificationType.NewContent:
          case NotificationType.UserConfirmed:
          case NotificationType.UserRoleChanged:
          case NotificationType.UserInvited:
          case NotificationType.ProjectAssigned:
          case NotificationType.CardComment:
          case NotificationType.CommentReply:
          case NotificationType.AvailableCommand:
          case NotificationType.CommandRequestStatusChanged:
          case NotificationType.ContentRequestCreated:
          case NotificationType.ContentRequestUpdated:
          case NotificationType.ContentRequestCommented:
            return true;
          default:
            return false;
        }
      });
    const todayNotifications = allNotifications.filter((notification) => notification.date >= todayTimestamp);
    const thisWeekNotifications = allNotifications.filter((notification) => notification.date >= thisWeekTimestamp && notification.date < todayTimestamp);
    const olderNotifications = allNotifications.filter((notification) => notification.date < thisWeekTimestamp);
    return [
      ...(todayNotifications.length > 0 ? [{ title: 'today', data: todayNotifications }] : []),
      ...(thisWeekNotifications.length > 0 ? [{ title: 'this-week', data: thisWeekNotifications }] : []),
      ...(olderNotifications.length > 0 ? [{ title: 'older', data: olderNotifications }] : []),
    ];
  }, [notifications.data]);

  const acknowledgeAllMutation = useAcknowledgeAllNotificationsMutation({
    onSuccess() {
      queryClient.invalidateQueries(['identityNotifications.infinite']);
      setTimeout(() => {
        queryClient.invalidateQueries(['identityNumNotifications']);
      }, 3000);
    },
  });

  if (!notifications.data) {
    return <Loader />;
  }

  if (groupedNotifications.length === 0) {
    return <EmptyState />;
  }

  return (
    <div>
      {groupedNotifications.map((group, index) => (
        <Box className="today" key={group.title}>
          <Box className="header">
            <Typography variant="h6">{tCommon(group.title)}</Typography>
            {index === 0 && <Button onClick={() => { acknowledgeAllMutation.mutate({}); }}>{t('notifications.mark-all-as-read')}</Button>}
          </Box>
          {group.data.map((notif) => <NotificationBox key={notif.eventId} notification={notif} onClose={onClose} />)}
        </Box>
      ))}
    </div>
  );
}
